// src/pages/ledger/Print.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../core/api';
import { getTodayJalali } from '../../utils/factorHelpers';
import { computeLedger, readLedgerCache, FILTER_LABELS } from './ledgerUtils';
import './Print.css';

const API_BASE = api.defaults.baseURL || '';
const imgUrl = (p) => {
  if (!p) return null;
  return /^(https?:|blob:|data:)/.test(p) ? p : `${API_BASE}${p}`;
};

const FA = '۰۱۲۳۴۵۶۷۸۹';
const toFa = (v) =>
  v === undefined || v === null ? '' : String(v).replace(/\d/g, (d) => FA[+d]);
const money = (n) => toFa(Math.round(Number(n) || 0).toLocaleString('en-US'));

// مانده: مثبت = بدهکار ، منفی = بستانکار
const balanceText = (b) => {
  const v = Math.round(Number(b) || 0);
  if (!v) return '۰';
  return `${money(Math.abs(v))}`;
};

const PAGE_CSS = `
@page { size: A4 portrait; margin: 0; }
@media print {
  html, body { width: 210mm; background: none !important; margin: 0 !important; padding: 0 !important; }
}
`;

const MM = 96 / 25.4;
const mm = (n) => n * MM;
const PAGE_H_MM = 296;
const CAP = mm(PAGE_H_MM - 10 - 14) - 4;

const COLS = [11, 9, 12, 12, 12, 14, 30];

function Header({ seller, logoUrl, data, from, to, filter }) {
  return (
    <div className="lg-header">
      <div className="lg-header-top">
        <div className="lg-logo-box">
          {logoUrl && <img src={logoUrl} alt="لوگو" className="lg-logo" />}
        </div>
        <div className="lg-title-box">
          <h1>{seller.name || ''}</h1>
          <h2>
            دفتر کل - گزارش مالی
            {filter !== 'all' && ` (${FILTER_LABELS[filter]})`}
          </h2>
          <div className="lg-customer">
            طرف حساب: <strong>{data.customer.name}</strong>
          </div>
        </div>
        <div />
      </div>

      <div className="lg-info-bar">
        <div>
          <span>بازه زمانی: </span>
          {from || to ? (
            <span>
              {from && (<>از تاریخ <strong>{toFa(from)}</strong> </>)}
              {to && (<>تا تاریخ <strong>{toFa(to)}</strong></>)}
            </span>
          ) : (
            <strong>تمام سوابق</strong>
          )}
        </div>
        <div>
          <span>تاریخ گزارش: </span>
          <strong>{toFa(getTodayJalali())}</strong>
        </div>
      </div>
    </div>
  );
}

function THead() {
  return (
    <thead>
      <tr>
        <th style={{ width: `${COLS[1]}%` }}>تاریخ</th>
        <th style={{ width: `${COLS[1]}%` }}>شماره</th>
        <th style={{ width: `${COLS[6]}%` }}>شرح</th>
        <th style={{ width: `${COLS[3]}%` }}>بدهکار</th>
        <th style={{ width: `${COLS[4]}%` }}>بستانکار</th>
        <th style={{ width: `${COLS[1]}%` }}>تشخیص</th>
        <th style={{ width: `${COLS[5]}%` }}>مانده</th>
      </tr>
    </thead>
  );
}

function Row({ r }) {
  if (r.type === 'opening') {
    return (
      <tr className="lg-r lg-opening">
        <td colSpan="5" style={{ textAlign: 'right', paddingRight: 10 }}>
          مانده از قبل
        </td>
        <td>{balanceText(r.balance)}</td>
        <td />
      </tr>
    );
  }
  if (r.type === 'empty') {
    return (
      <tr className="lg-r">
        <td colSpan="7">گردشی در این بازه ثبت نشده است.</td>
      </tr>
    );
  }

  const isFactor = r.kind === 'factor';
  const desc = isFactor
    ? `فاکتور${r.paymentType ? ` (${r.paymentType})` : ''} - ${r.description}`
    : `${r.direction === 'receive' ? 'دریافت' : 'پرداخت'} - ${r.description}`;

  return (
    <tr className="lg-r">
      <td>{toFa(r.date)}</td>
      <td>{toFa(r.ref)}</td>
      <td className="lg-desc">{desc}</td>
      <td>{r.debit ? money(r.debit) : '.'}</td>
      <td>{r.credit ? money(r.credit) : '.'}</td>
      <td>{(r.balance == 0 ? "تسویه" : (r.balance > 0 ? "بدهکار" : "بستانکار"))}</td>
      <td>{balanceText(r.balance)}</td>
    </tr>
  );
}

function TotalRow({ data }) {
  const c = Math.round(Number(data.closing) || 0);
  const verdict = c > 0 ? 'بدهکار' : c < 0 ? 'بستانکار' : 'تسویه';

  return (
    <tr className="lg-total">
      <td colSpan="2">جمع کل</td>
      <td />
      <td>{money(data.totalDebit)}</td>
      <td>{money(data.totalCredit)}</td>
      <td>{verdict}</td>
      <td>{balanceText(data.closing)}</td>
    </tr>
  );
}

export default function LedgerPrint() {
  const { customerId } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const from = sp.get('from') || '';
  const to = sp.get('to') || '';
  const rawFilter = sp.get('filter');
  const filter = ['factor', 'transaction'].includes(rawFilter) ? rawFilter : 'all';

  const [raw, setRaw] = useState(null);
  const [seller, setSeller] = useState({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');
  const [pages, setPages] = useState(null);

  const measureRef = useRef(null);

  /* ============== @page مخصوص همین صفحه ============== */
  useEffect(() => {
    const el = document.createElement('style');
    el.setAttribute('data-lg-page', '');
    el.textContent = PAGE_CSS;
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  /* ============== داده: اول از کش صفحه قبل، در غیر این صورت از سرور ============== */
  useEffect(() => {
    let cancelled = false;
    setPages(null);

    const cached = readLedgerCache(customerId);

    (async () => {
      try {
        if (cached?.raw) {
          setRaw(cached.raw);
          if (cached.seller) {
            setSeller(cached.seller);
          } else {
            const cRes = await api.get('/company').catch(() => ({ data: {} }));
            if (!cancelled) setSeller(cRes.data?.company || {});
          }
        } else {
          const [lRes, cRes] = await Promise.all([
            api.get(`/ledger/${customerId}`),
            api.get('/company').catch(() => ({ data: {} })),
          ]);
          if (cancelled) return;
          setRaw(lRes.data);
          setSeller(cRes.data?.company || {});
        }
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404) setNotFound(true);
        else setError('خطا در دریافت دفتر کل');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [customerId]);

  /* ============== فیلتر نوع + بازه + مانده ============== */
  const data = useMemo(
    () => (raw ? computeLedger(raw, { from, to, filter }) : null),
    [raw, from, to, filter]
  );

  /* ============== ردیف‌ها ============== */
  const rows = useMemo(() => {
    if (!data) return [];
    const list = [];
    if (from || Number(data.opening) !== 0) {
      list.push({ type: 'opening', balance: data.opening });
    }
    data.rows.forEach((r) => list.push({ type: 'row', ...r }));
    if (list.length === 0) list.push({ type: 'empty' });
    return list;
  }, [data, from]);

  const logoUrl = imgUrl(seller.logo);

  useEffect(() => {
    if (data) document.title = `دفتر کل ${data.customer.name} | پنل فاکتور`;
  }, [data]);

  /* ============== اندازه‌گیری و صفحه‌بندی ============== */
  useEffect(() => {
    if (!data || pages) return undefined;
    let cancelled = false;

    const run = async () => {
      if (document.fonts?.ready) await document.fonts.ready;
      const img = measureRef.current?.querySelector('img');
      if (img && !img.complete) {
        await new Promise((res) => {
          img.onload = res;
          img.onerror = res;
        });
      }
      if (cancelled) return;
      const m = measureRef.current;
      if (!m) return;

      const headerH = m.querySelector('.lg-header').getBoundingClientRect().height + 10;
      const theadH = m.querySelector('thead').getBoundingClientRect().height;
      const rowHs = Array.from(m.querySelectorAll('tbody tr.lg-r')).map(
        (t) => t.getBoundingClientRect().height
      );
      const totalH = m.querySelector('tr.lg-total').getBoundingClientRect().height;

      const pgs = [];
      let cur = [];
      let used = headerH + theadH;
      rowHs.forEach((h, i) => {
        if (cur.length && used + h > CAP) {
          pgs.push(cur);
          cur = [];
          used = theadH;
        }
        cur.push(i);
        used += h;
      });

      if (used + totalH > CAP) {
        if (cur.length > 1) {
          const moved = cur.pop();
          pgs.push(cur);
          cur = [moved];
        } else {
          pgs.push(cur);
          cur = [];
        }
      }
      pgs.push(cur);

      if (!cancelled) setPages(pgs);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [data, pages, rows]);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else {
      window.close();
      setTimeout(() => navigate('/dashboard/ledger'), 150);
    }
  };

  if (loading || notFound || error || !data) {
    return (
      <div className="lg-wrap">
        <div className="lg-status">
          {loading && 'در حال بارگذاری دفتر کل...'}
          {notFound && 'مشتری یافت نشد'}
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="lg-wrap">
      <div className="lg-actions">
        <button type="button" className="lg-btn print" onClick={() => window.print()} disabled={!pages}>
          <i className="fa-solid fa-print" /> چاپ
        </button>
        <button type="button" className="lg-btn back" onClick={goBack}>
          <i className="fa-solid fa-arrow-right" /> بازگشت
        </button>
      </div>

      {!pages && (
        <div className="lg-measure" ref={measureRef}>
          <Header seller={seller} logoUrl={logoUrl} data={data} from={from} to={to} filter={filter} />
          <table className="lg-table">
            <THead />
            <tbody>
              {rows.map((r, i) => (
                <Row key={i} r={r} />
              ))}
              <TotalRow data={data} />
            </tbody>
          </table>
        </div>
      )}

      {!pages && <div className="lg-status">در حال آماده‌سازی صفحات...</div>}

      {pages &&
        pages.map((idxs, p) => (
          <div className="lg-page" key={p}>
            {p === 0 && (
              <Header seller={seller} logoUrl={logoUrl} data={data} from={from} to={to} filter={filter} />
            )}
            <table className="lg-table">
              <THead />
              <tbody>
                {idxs.map((i) => (
                  <Row key={i} r={rows[i]} />
                ))}
                {p === pages.length - 1 && <TotalRow data={data} />}
              </tbody>
            </table>
            <div className="lg-pn">
              صفحه {(p + 1).toLocaleString('fa-IR')} از {pages.length.toLocaleString('fa-IR')}
            </div>
          </div>
        ))}
    </div>
  );
}